import {
  FeatureIncludedCountFragment,
  useFeatureIncludedCountQuery
} from "../../api/generated"
import {
  BILLING_MODEL_HYBRID,
  BILLING_MODEL_PAYG
} from "../../billing/constants"

export type UpgradePrompt =
  // show an activation prompt
  | "activate"
  // show an upgrade prompt
  | "upgrade"
  // show no prompt
  | null

// these keys correspond to fields in the FeatureIncludedCountQuery
type ExtractExtraKey<T> = T extends `extra_${infer Key}` ? Key : never

export type UgragePromptKey = ExtractExtraKey<
  keyof NonNullable<FeatureIncludedCountFragment["billing_subscription"]>
>

// number of items included by default for any school
const DEFAULT_INCLUDED: Record<UgragePromptKey, number> = {
  cohorts: 1,
  mentor_groups: 1
}

type UseUpgradePromptProps = {
  key: UgragePromptKey
  totalUsed: number
}

type UseUpgradePromptReturn = {
  loading: boolean
  prompt: UpgradePrompt
  totalIncluded: null | number
}

// util hook for determing what kind of prompt the user should be given
// depending on usage of a specific feature
const useUpgradePrompt = ({
  key,
  totalUsed
}: UseUpgradePromptProps): UseUpgradePromptReturn => {
  let prompt: UpgradePrompt = null
  let totalIncluded: number | null = null

  const featureIncludedCountQuery = useFeatureIncludedCountQuery()
  const { loading, data } = featureIncludedCountQuery

  if (data) {
    const { account } = data

    // default included count on the plan
    const included =
      account?.billing_subscription?.plan?.[`${key}_included`] ||
      DEFAULT_INCLUDED[key]

    // extra count added to the school's billing subscription
    const extra = account?.billing_subscription?.[`extra_${key}`] || 0

    // total included is the base + extra
    totalIncluded = included + extra

    if (account?.is_activated) {
      if (account.is_activated_via_override) totalIncluded = Infinity
      if (account.billing_model === BILLING_MODEL_PAYG) totalIncluded = Infinity
      if (included === -1) totalIncluded = Infinity

      // Hybrid included count yet to be determined, but assumption is
      // that if the included count is the default, then we must not have set any by default and treating as unlimited
      if (
        account.billing_model === BILLING_MODEL_HYBRID &&
        totalIncluded <= DEFAULT_INCLUDED[key]
      )
        totalIncluded = Infinity
    }

    if (totalUsed >= totalIncluded) {
      if (!account?.is_activated) {
        prompt = "activate"
      } else {
        prompt = "upgrade"
      }
    } else {
      // no prompt is necessary
      prompt = null
    }
  }

  return {
    loading,
    prompt,
    totalIncluded
  }
}

export default useUpgradePrompt
